import { shouldIgnorePermissionGraphSnapshot } from './permission-graph-version';

const DEFAULT_RETRY_DELAYS_MS = [100, 250, 500, 1000, 2000] as const;

type PermissionGraphCatchUpOptions<T> = {
  read: () => Promise<T>;
  getIncomingVersion: (value: T) => number;
  getCurrentVersion: () => number;
  signal?: AbortSignal;
  retryDelaysMs?: readonly number[];
};

export async function readPermissionGraphSnapshotWithCatchUp<T>({
  read,
  getIncomingVersion,
  getCurrentVersion,
  signal,
  retryDelaysMs = DEFAULT_RETRY_DELAYS_MS,
}: PermissionGraphCatchUpOptions<T>): Promise<T | null> {
  let value = await read();
  for (const delayMs of retryDelaysMs) {
    if (
      !shouldIgnorePermissionGraphSnapshot(
        getIncomingVersion(value),
        getCurrentVersion()
      )
    ) {
      return value;
    }
    await abortableDelay(delayMs, signal);
    value = await read();
  }

  return shouldIgnorePermissionGraphSnapshot(
    getIncomingVersion(value),
    getCurrentVersion()
  )
    ? null
    : value;
}

function abortableDelay(ms: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) {
    return Promise.reject(abortReason(signal));
  }
  if (!signal) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
  return new Promise((resolve, reject) => {
    const onAbort = () => {
      clearTimeout(timer);
      reject(abortReason(signal));
    };
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

function abortReason(signal: AbortSignal): unknown {
  return signal.reason ?? new DOMException('Aborted', 'AbortError');
}
